import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import EditUserModal from '../../components/User/EditUserModal';
import EmailSystemTab from '../../components/Admin/EmailSystemTab';

const AdminArea = ({ isOpen, onClose }) => {
  const {
    setupData,
    toggleLogin,
    toggleRegister,
    createUser,
    getUsers,
    updateUser,
    deleteUser,
    toggleUserActive,
    checkSetup,
    user: currentUser
  } = useAuth();
  const { showSuccess, showError } = useToast();
  const [registerEnabled, setRegisterEnabled] = useState(false);
  const [loginEnabled, setLoginEnabled] = useState(true);
  const [showCreateUserForm, setShowCreateUserForm] = useState(false);
  const [createUserData, setCreateUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [editingUser, setEditingUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (setupData) {
      setRegisterEnabled(setupData.registerEnabled);
      setLoginEnabled(
        setupData.loginEnabled !== undefined ? setupData.loginEnabled : true
      );
    }
  }, [setupData]);

  // Admin Area açıldığında kullanıcıları yükle
  useEffect(() => {
    if (isOpen && activeTab === 'users') {
      loadUsers();
    }
  }, [isOpen, activeTab]);

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const result = await getUsers();
      if (result.success) {
        setUsers(result.data.users);
      } else {
        showError(result.error);
      }
    } catch (error) {
      showError('Kullanıcı listesi yüklenemedi');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleToggleLogin = async () => {
    setLoading(true);
    try {
      const newState = !loginEnabled;
      const result = await toggleLogin(newState);

      if (result.success) {
        setLoginEnabled(newState);
        showSuccess(
          newState
            ? 'Normal kullanıcı girişi açıldı'
            : 'Normal kullanıcı girişi kapatıldı (yalnızca yöneticiler giriş yapabilir)'
        );
      } else {
        showError(result.error);
      }
    } catch (error) {
      showError('Ayar güncelleme hatası');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRegister = async () => {
    setLoading(true);
    try {
      const newState = !registerEnabled;
      const result = await toggleRegister(newState);
      
      if (result.success) {
        setRegisterEnabled(newState);
        showSuccess(
          newState 
            ? 'Kullanıcı kaydı açıldı' 
            : 'Kullanıcı kaydı kapatıldı'
        );
      } else {
        showError(result.error);
      }
    } catch (error) {
      showError('Ayar güncelleme hatası');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (createUserData.password.length < 6) {
      showError('Şifre en az 6 karakter olmalıdır!');
      return;
    }

    setLoading(true);
    try {
      const result = await createUser(createUserData);
      
      if (result.success) {
        showSuccess('Yeni kullanıcı başarıyla oluşturuldu!');
        setCreateUserData({
          firstName: '',
          lastName: '',
          email: '',
          password: ''
        });
        setShowCreateUserForm(false);
        loadUsers();
      } else {
        showError(result.error);
      }
    } catch (error) {
      showError('Kullanıcı oluşturma hatası');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setCreateUserData({
      ...createUserData,
      [e.target.name]: e.target.value
    });
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  const handleUpdateUser = async (userData) => {
    setLoading(true);
    try {
      const result = await updateUser(editingUser.id, userData);
      
      if (result.success) {
        showSuccess('Kullanıcı başarıyla güncellendi!');
        setShowEditModal(false);
        setEditingUser(null);
        loadUsers(); // Listeyi yenile
      } else {
        showError(result.error);
      }
    } catch (error) {
      showError('Kullanıcı güncelleme hatası');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`"${userName}" kullanıcısını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) {
      return;
    }

    setLoading(true);
    try {
      const result = await deleteUser(userId);
      
      if (result.success) {
        showSuccess('Kullanıcı başarıyla silindi!');
        loadUsers(); // Listeyi yenile
      } else {
        showError(result.error);
      }
    } catch (error) {
      showError('Kullanıcı silme hatası');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserActive = async (userId, isActive, userName) => {
    // Admin kullanıcılar pasif yapılamaz
    const user = users.find(u => u.id === userId);
    if (user.isAdmin && !isActive) {
      showError('Admin kullanıcı pasif yapılamaz');
      return;
    }

    // Kendini pasif yapmaya çalışıyor mu?
    if (userId === currentUser?.id && !isActive) {
      showError('Kendi hesabınızı pasif yapamazsınız');
      return;
    }

    setLoading(true);
    try {
      const result = await toggleUserActive(userId, isActive);
      
      if (result.success) {
        showSuccess(isActive 
          ? `"${userName}" kullanıcısı aktif edildi` 
          : `"${userName}" kullanıcısı pasif edildi`);
        loadUsers(); // Listeyi yenile
      } else {
        showError(result.error);
      }
    } catch (error) {
      showError('Kullanıcı durumu değiştirme hatası');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Admin Area</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-1 mb-6 bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => setActiveTab('settings')}
              className={`flex-1 min-w-[100px] px-3 py-2 rounded-md font-medium text-sm transition-colors ${
                activeTab === 'settings'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              ⚙️ Ayarlar
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('users')}
              className={`flex-1 min-w-[100px] px-3 py-2 rounded-md font-medium text-sm transition-colors ${
                activeTab === 'users'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              👥 Kullanıcılar
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('email')}
              className={`flex-1 min-w-[100px] px-3 py-2 rounded-md font-medium text-sm transition-colors ${
                activeTab === 'email'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              ✉️ E-posta sistemi
            </button>
          </div>

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              {/* Register Ayarı */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Kullanıcı Kaydı Ayarları</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-700 font-medium">Yeni kullanıcı kaydına izin ver</p>
                    <p className="text-sm text-gray-500">
                      Bu ayar kapalıyken yeni kullanıcılar kayıt olamaz
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={registerEnabled}
                      onChange={handleToggleRegister}
                      disabled={loading}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>

              {/* Giriş ayarı */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Kullanıcı Girişi Ayarları</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-700 font-medium">Normal kullanıcıların giriş yapmasına izin ver</p>
                    <p className="text-sm text-gray-500">
                      Kapalıyken yalnızca yönetici hesapları giriş yapabilir
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={loginEnabled}
                      onChange={handleToggleLogin}
                      disabled={loading}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>

              {/* İstatistikler */}
              {setupData && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Sistem İstatistikleri</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg p-4 border">
                      <div className="text-2xl font-bold text-blue-600">{setupData.userCount}</div>
                      <div className="text-sm text-gray-600">Toplam Kullanıcı</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border">
                      <div className="text-2xl font-bold text-green-600">
                        {registerEnabled ? 'Açık' : 'Kapalı'}
                      </div>
                      <div className="text-sm text-gray-600">Kullanıcı Kaydı</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border">
                      <div className="text-2xl font-bold text-amber-600">
                        {loginEnabled ? 'Açık' : 'Kapalı'}
                      </div>
                      <div className="text-sm text-gray-600">Normal Kullanıcı Girişi</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* E-posta sistemi */}
          {activeTab === 'email' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">E-posta gönderim ayarları</h3>
              <EmailSystemTab />
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              {/* Kullanıcı Yönetimi — yeni kullanıcı */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Kullanıcı Yönetimi</h3>

                {!showCreateUserForm ? (
                  <button
                    onClick={() => setShowCreateUserForm(true)}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <span>Yeni Kullanıcı Oluştur</span>
                  </button>
                ) : (
                  <form onSubmit={handleCreateUser} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-2">
                          Ad
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={createUserData.firstName}
                          onChange={handleInputChange}
                          required
                          autoFocus
                          autoComplete="off"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Ad"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-2">
                          Soyad
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={createUserData.lastName}
                          onChange={handleInputChange}
                          required
                          autoComplete="off"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Soyad"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-2">
                        E-mail
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={createUserData.email}
                        onChange={handleInputChange}
                        required
                        autoComplete="off"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="E-mail adresi"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-2">
                        Şifre
                      </label>
                      <input
                        type="password"
                        name="password"
                        value={createUserData.password}
                        onChange={handleInputChange}
                        required
                        autoComplete="off"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Şifre (min 6 karakter)"
                      />
                    </div>

                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowCreateUserForm(false);
                          setCreateUserData({
                            firstName: '',
                            lastName: '',
                            email: '',
                            password: ''
                          });
                        }}
                        className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                      >
                        İptal
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? (
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                            Oluşturuluyor...
                          </div>
                        ) : (
                          'Kullanıcı Oluştur'
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">Kullanıcı Listesi</h3>
                <button
                  onClick={loadUsers}
                  disabled={usersLoading}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Yenile</span>
                </button>
              </div>

              {usersLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Kullanıcılar yükleniyor...</p>
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600">Henüz kullanıcı bulunmuyor.</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Kullanıcı
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            E-mail
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Rol
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Durum
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Kayıt Tarihi
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            İşlemler
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {users.map((user) => (
                          <tr key={user.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                  {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                                </div>
                                <div className="ml-3">
                                  <div className="text-sm font-medium text-gray-900">
                                    {user.firstName} {user.lastName}
                                  </div>
                                  {user.id === currentUser?.id && (
                                    <div className="text-xs text-blue-600">(Siz)</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {user.email}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {user.isAdmin ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  👑 Admin
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  👤 Kullanıcı
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={user.isActive !== false}
                                    onChange={() => handleToggleUserActive(
                                      user.id, 
                                      !user.isActive, 
                                      `${user.firstName} ${user.lastName}`
                                    )}
                                    disabled={loading || (user.isAdmin && user.isActive !== false) || (user.id === currentUser?.id && user.isActive !== false)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                                </label>
                                <span className="ml-3 text-sm font-medium text-gray-900">
                                  {user.isActive !== false ? (
                                    <span className="text-green-600">Aktif</span>
                                  ) : (
                                    <span className="text-red-600">Pasif</span>
                                  )}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(user.createdAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end space-x-2">
                                <button
                                  onClick={() => handleEditUser(user)}
                                  className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                                  title="Düzenle"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                
                                {/* Admin kullanıcıları ve kendisi silinemesin */}
                                {!user.isAdmin && user.id !== currentUser?.id && (
                                  <button
                                    onClick={() => handleDeleteUser(user.id, `${user.firstName} ${user.lastName}`)}
                                    disabled={loading}
                                    className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 disabled:opacity-50"
                                    title="Sil"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit User Modal */}
      <EditUserModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingUser(null);
        }}
        user={editingUser}
        onSave={handleUpdateUser}
        loading={loading}
      />
    </>
  );
};

export default AdminArea;
